<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Department Appraisals</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Performance</a></li>
                    <li class="breadcrumb-item active">Manage Appraisals</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Manager Info Card -->
          <div class="row mb-3" v-if="managerInfo">
            <div class="col-12">
              <div class="card bg-primary text-white">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-md-8">
                      <h5 class="text-white mb-2">{{ managerInfo.name }}</h5>
                      <p class="mb-0">
                        <strong>Position:</strong> {{ managerInfo.positionTitle }} |
                        <strong>Department:</strong> {{ managerInfo.department }}
                      </p>
                    </div>
                    <div class="col-md-4 text-end">
                      <h3 class="text-white mb-0">{{ statistics.total }}</h3>
                      <small>Total Appraisals</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Statistics Cards -->
          <div class="row mb-4">
            <div class="col-xl-3 col-md-6">
              <div class="card card-h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <div>
                      <h4 class="mb-1">{{ statistics.pending }}</h4>
                      <p class="text-muted mb-0">Pending Review</p>
                    </div>
                    <div class="avatar-sm">
                      <span class="avatar-title bg-warning rounded-circle">
                        <i class="mdi mdi-clock-outline font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <div>
                      <h4 class="mb-1">{{ statistics.completed }}</h4>
                      <p class="text-muted mb-0">Completed</p>
                    </div>
                    <div class="avatar-sm">
                      <span class="avatar-title bg-success rounded-circle">
                        <i class="mdi mdi-check-circle-outline font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <div>
                      <h4 class="mb-1">{{ statistics.inProgress }}</h4>
                      <p class="text-muted mb-0">In Progress</p>
                    </div>
                    <div class="avatar-sm">
                      <span class="avatar-title bg-info rounded-circle">
                        <i class="mdi mdi-progress-clock font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <div>
                      <h4 class="mb-1">{{ statistics.avgRating }}</h4>
                      <p class="text-muted mb-0">Avg Rating</p>
                    </div>
                    <div class="avatar-sm">
                      <span class="avatar-title bg-primary rounded-circle">
                        <i class="mdi mdi-star-outline font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Filters -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-md-3">
                      <label class="form-label">Filter by Status</label>
                      <select v-model="filters.status" @change="loadAppraisals" class="form-select">
                        <option value="">All Statuses</option>
                        <option value="Self-Assessment Complete">Pending My Review</option>
                        <option value="Appraiser Review Complete">Reviewed by Me</option>
                        <option value="Reviewer Complete">Final Review Done</option>
                        <option value="Approved">Approved</option>
                        <option value="Draft">Draft</option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Filter by Year</label>
                      <select v-model="filters.year" @change="loadAppraisals" class="form-select">
                        <option value="">All Years</option>
                        <option value="2025">2025</option>
                        <option value="2024">2024</option>
                        <option value="2023">2023</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Search Employee</label>
                      <input
                          type="text"
                          v-model="filters.search"
                          @input="searchAppraisals"
                          class="form-control"
                          placeholder="Search by employee name..."
                      >
                    </div>
                    <div class="col-md-2 d-flex align-items-end">
                      <button @click="clearFilters" class="btn btn-secondary w-100">
                        <i class="mdi mdi-refresh me-1"></i> Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Appraisals Table -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <h5 class="card-title mb-3">Department Appraisals</h5>

                  <!-- Loading State -->
                  <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-2">Loading appraisals...</p>
                  </div>

                  <!-- Appraisals List -->
                  <div v-else-if="filteredAppraisals.length > 0" class="table-responsive">
                    <table class="table table-hover align-middle">
                      <thead class="table-light">
                      <tr>
                        <th>Employee</th>
                        <th>Position</th>
                        <th>Period</th>
                        <th>Year</th>
                        <th>Status</th>
                        <th>Self Rating</th>
                        <th>My Rating</th>
                        <th>Submitted</th>
                        <th class="text-center">Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="appraisal in filteredAppraisals" :key="appraisal.id">
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="avatar-sm me-2">
                                <span class="avatar-title rounded-circle bg-primary text-white">
                                  {{ getInitials(appraisal.employeeName) }}
                                </span>
                            </div>
                            <div>
                              <h6 class="mb-0">{{ appraisal.employeeName }}</h6>
                            </div>
                          </div>
                        </td>
                        <td>{{ appraisal.positionTitle }}</td>
                        <td>
                          <small>{{ appraisal.periodStart }} - {{ appraisal.periodEnd }}</small>
                        </td>
                        <td>{{ appraisal.appraisalYear }}</td>
                        <td>
                            <span :class="getStatusBadgeClass(appraisal.status)" class="badge">
                              {{ appraisal.status }}
                            </span>
                        </td>
                        <td>
                            <span v-if="appraisal.sectionA_SelfOverallRating" class="badge bg-primary">
                              {{ parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(2) }}
                            </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                            <span v-if="appraisal.sectionA_AppraiserOverallRating" class="badge bg-success">
                              {{ parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(2) }}
                            </span>
                          <span v-else class="text-muted">Pending</span>
                        </td>
                        <td>
                          <small>{{ appraisal.submittedDate || '-' }}</small>
                        </td>
                        <td class="text-center">
                          <div class="btn-group btn-group-sm">
                            <button
                                @click="viewAppraisal(appraisal.id)"
                                class="btn btn-outline-info"
                                title="View Details"
                            >
                              <i class="mdi mdi-eye"></i>
                            </button>

                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Empty State -->
                  <div v-else class="text-center py-5">
                    <i class="mdi mdi-folder-open-outline display-4 text-muted"></i>
                    <h5 class="mt-3">No Appraisals Found</h5>
                    <p class="text-muted">There are no appraisals matching your filters.</p>
                  </div>

                </div>
              </div>
            </div>
          </div>

        </div>
        <!-- end container-fluid -->
      </div>
      <!-- End Page-content -->
    </div>
    <!-- end content-minimize -->
  </div>
  <!-- end layout-wrapper -->
</template>

<script>
import { authUrl, execute } from "@/api";
import NavigationBar from "@/components/Navigation";

export default {
  name: "ManagerDashboard",

  components: {
    NavigationBar
  },

  data() {
    return {
      loading: false,
      managerInfo: null,
      appraisals: [],
      filteredAppraisals: [],
      filters: {
        status: '',
        year: '',
        search: ''
      },
      statistics: {
        total: 0,
        pending: 0,
        completed: 0,
        inProgress: 0,
        avgRating: '0.00'
      }
    };
  },

  mounted() {
    this.loadManagerInfo();
  },

  methods: {
    async loadManagerInfo() {
      try {
        const data = new FormData();
        data.append("function", "getManagerInfo");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.managerInfo = res.data.data;
          this.loadAppraisals();
        } else {
          this.$toast?.error("Failed to load manager info") || alert("Failed to load manager info");
        }
      } catch (error) {
        console.error("Failed to load manager info:", error);
        this.$toast?.error("Error loading manager info") || alert("Error loading manager info");
      }
    },

    async loadAppraisals() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getDepartmentAppraisals");
        data.append("status", this.filters.status);
        data.append("year", this.filters.year);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.appraisals = res.data.data;
          this.applyFilters();
          this.calculateStatistics();
        } else {
          this.appraisals = [];
        }
      } catch (error) {
        console.error("Failed to load appraisals:", error);
        this.$toast?.error("Failed to load appraisals") || alert("Failed to load appraisals");
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      let filtered = [...this.appraisals];

      // Search filter
      if (this.filters.search) {
        const search = this.filters.search.toLowerCase();
        filtered = filtered.filter(a =>
            a.employeeName.toLowerCase().includes(search) ||
            a.positionTitle.toLowerCase().includes(search)
        );
      }

      this.filteredAppraisals = filtered;
    },

    searchAppraisals() {
      this.applyFilters();
    },

    clearFilters() {
      this.filters = {
        status: '',
        year: '',
        search: ''
      };
      this.loadAppraisals();
    },

    calculateStatistics() {
      this.statistics.total = this.appraisals.length;

      this.statistics.pending = this.appraisals.filter(a =>
          a.status === 'Self-Assessment Complete' || a.status === 'Appraiser Review Pending'
      ).length;

      this.statistics.completed = this.appraisals.filter(a =>
          a.status === 'Appraiser Review Complete' || a.status === 'Approved'
      ).length;

      this.statistics.inProgress = this.appraisals.filter(a =>
          a.status === 'Reviewer Pending' || a.status === 'HR/CEO Review Pending'
      ).length;

      // Calculate average rating
      const ratingsWithValues = this.appraisals.filter(a => a.sectionA_AppraiserOverallRating);
      if (ratingsWithValues.length > 0) {
        const sum = ratingsWithValues.reduce((acc, a) => acc + parseFloat(a.sectionA_AppraiserOverallRating), 0);
        this.statistics.avgRating = (sum / ratingsWithValues.length).toFixed(2);
      } else {
        this.statistics.avgRating = '0.00';
      }
    },

    viewAppraisal(id) {
      this.$router.push({
        path: '/manager-appraisal-view',
        query: { id: id }
      });
    },

    reviewAppraisal(id) {
      this.$router.push({
        path: '/manager-appraisal-review',
        query: { id: id }
      });
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Draft': 'bg-secondary',
        'Self-Assessment Complete': 'bg-warning',
        'Appraiser Review Pending': 'bg-warning',
        'Appraiser Review Complete': 'bg-info',
        'Reviewer Pending': 'bg-info',
        'Reviewer Complete': 'bg-primary',
        'HR/CEO Review Pending': 'bg-primary',
        'Approved': 'bg-success',
        'Rejected': 'bg-danger'
      };
      return classes[status] || 'bg-secondary';
    },

    getInitials(name) {
      if (!name) return '?';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
  }
};
</script>

<style scoped>
.card-h-100 {
  height: calc(100% - 0px);
}

.avatar-sm {
  height: 40px;
  width: 40px;
}

.avatar-title {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
  font-weight: 600;
}

.table > :not(caption) > * > * {
  padding: 0.75rem 0.75rem;
}
</style>